import { useMemo, useState } from "react";
import { Button, Chip, Icon, Modal, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import {
  optOuts,
  type ImportOptOutsResponse,
  type ImportPreviewRow,
  type ValidateImportResponse,
} from "@/api/optOuts.service";
import { usePublications } from "@/hooks/usePublications";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import {
  buildLegacyPublicationLookup,
  buildNewAxisEditionLookup,
  hasStoredEditionUuid,
  resolveStoredPublicationLabel,
} from "./optOutPublications.utils";
import styles from "./OptOuts.module.css";

type ImportPreviewModalProps = {
  /** Raw CSV text — re-sent verbatim to the confirm endpoint on Accept. */
  csv: string;
  preview: ValidateImportResponse;
  onClose: () => void;
  onConfirmed: (result: ImportOptOutsResponse) => void;
};

/** Rows rendered in the preview table — the counts line above it states the real totals. */
const PREVIEW_ROW_LIMIT = 100;

/** Validation errors rendered in the list — enough to judge the file without burying the footer. */
const VALIDATION_ERROR_LIMIT = 100;

const SCOPE_LABEL: Record<string, string> = {
  firm: "firm",
  edition: "publication",
  global: "all emails",
};

function describeScope(
  row: ImportPreviewRow,
  publicationLabel: (storedId: string | null | undefined) => string,
): string {
  const label = SCOPE_LABEL[row.scope] ?? row.scope;
  if (row.scope === "firm") {
    const pub = publicationLabel(row.edition);
    return `${label} (${row.firm_ref ?? "?"} / ${pub})`;
  }
  if (row.scope === "edition") {
    return `${label} (${publicationLabel(row.edition)})`;
  }
  return label;
}

export function ImportPreviewModal({
  csv,
  preview,
  onClose,
  onConfirmed,
}: ImportPreviewModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: publicationsData } = usePublications({});
  const publicationLookup = useMemo(
    () => buildLegacyPublicationLookup(publicationsData?.publications ?? []),
    [publicationsData?.publications],
  );
  const { toggleVisible } = useTaxonomyMode();
  const previewHasEditionUuid = useMemo(
    () => hasStoredEditionUuid(preview.preview.map(row => row.edition)),
    [preview.preview],
  );
  const { data: newAxisEditions } = useNewAxisEditions({
    enabled: toggleVisible && previewHasEditionUuid,
  });
  const newAxisEditionLookup = useMemo(
    () => buildNewAxisEditionLookup(newAxisEditions),
    [newAxisEditions],
  );
  const publicationLabel = (storedId: string | null | undefined) =>
    resolveStoredPublicationLabel(storedId, publicationLookup, newAxisEditionLookup);

  const { total_rows, will_import, will_collapse, validation_errors } = preview;
  const hasErrors = validation_errors.length > 0;
  const visibleRows = preview.preview.slice(0, PREVIEW_ROW_LIMIT);
  const isTruncated = preview.preview.length > visibleRows.length;
  const visibleErrors = validation_errors.slice(0, VALIDATION_ERROR_LIMIT);
  const areErrorsTruncated = validation_errors.length > visibleErrors.length;

  const columns: Column<ImportPreviewRow>[] = [
    { header: "Row", accessor: "row" },
    {
      header: "Email",
      accessor: "email",
      render: (_value: unknown, row: ImportPreviewRow) => (
        <span className={row.action === "collapsed" ? styles.collapsedEmail : undefined}>
          {row.email}
        </span>
      ),
    },
    {
      header: "Scope",
      accessor: "scope",
      render: (_value: unknown, row: ImportPreviewRow) => describeScope(row, publicationLabel),
    },
    {
      header: "Result",
      accessor: "action",
      render: (_value: unknown, row: ImportPreviewRow) => {
        if (row.action === "collapsed") {
          const by = row.collapsed_by;
          return (
            <Chip size="small" variant="filled" color="warning">
              collapsed{by ? ` by row ${by.row} (${SCOPE_LABEL[by.scope] ?? by.scope})` : ""}
            </Chip>
          );
        }
        return (
          <Chip size="small" variant="filled" color="success">
            import
          </Chip>
        );
      },
    },
  ];

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await optOuts.importCsv(csv);
      onConfirmed(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to import CSV");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Review CSV import"
      size="large"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.previewBody}>
        <div className={styles.previewCounts}>
          <span>
            <strong>{total_rows}</strong> row{total_rows === 1 ? "" : "s"}
          </span>
          <span>
            <strong>{will_import}</strong> will be imported
          </span>
          <span>
            <strong>{will_collapse}</strong> will be collapsed
          </span>
        </div>

        {will_collapse > 0 && (
          <div className={styles.previewWarning}>
            <Icon icon="exclamation" size="small" />
            <span>
              Some rows overlap within the file — only the highest level per person/publication will
              be saved. Continue?
            </span>
          </div>
        )}

        {hasErrors && (
          <div className={styles.previewErrors}>
            <div className={styles.previewErrorsTitle}>
              <Icon icon="exclamation" size="small" />
              <span>{validation_errors.length} row(s) could not be validated</span>
            </div>
            <div className={styles.previewErrorsScroll}>
              <ul className={styles.importErrors}>
                {visibleErrors.map((e, index) => (
                  <li key={`err-${index}-${e.row}`}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
            {areErrorsTruncated && (
              <div className={styles.previewTruncated}>
                Showing the first {visibleErrors.length} of {validation_errors.length} errors.
              </div>
            )}
          </div>
        )}

        {isTruncated && (
          <div className={styles.previewTruncated}>
            Showing the first {visibleRows.length} of {preview.preview.length} rows.
          </div>
        )}

        <div className={styles.previewTableScroll}>
          <DataTable
            data={visibleRows}
            columns={columns}
            size="small"
            rowKey="row"
            emptyMessage="No rows to import."
          />
        </div>

        {error && (
          <div className={styles.formError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={submitting || will_import === 0}
          >
            {submitting ? "Importing…" : `Accept & import ${will_import}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
